import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Select,
  Input,
  Typography,
  message,
  Popconfirm,
  Tag,
  Collapse,
  Badge,
  Checkbox
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, EyeOutlined, ExportOutlined, ImportOutlined, SplitCellsOutlined, SwapOutlined, TagOutlined, ToolOutlined, SettingOutlined, MenuFoldOutlined, MenuUnfoldOutlined, HistoryOutlined } from '@ant-design/icons';
import { inventoryAPI, userColumnPreferencesAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatCurrency } from '../utils/numberFormatting.ts';
import InventoryStockModal from '../components/InventoryStockModal.tsx';
import StockReservationsModal from '../components/StockReservationsModal.tsx';
import StockHistoryModal from '../components/StockHistoryModal.tsx';
import StockSplitModal from '../components/StockSplitModal.tsx';
import WarehouseTransferModal from '../components/WarehouseTransferModal.tsx';
import UdcChangeModal from '../components/UdcChangeModal.tsx';
import InventoryAdjustmentModal from '../components/InventoryAdjustmentModal.tsx';
import InventoryImportModal from '../components/InventoryImportModal.tsx';

const { Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

interface InventoryStock {
  id: number;
  product_id: number;
  warehouse_id: number;
  customer_id?: number;
  supplier_id?: number;
  stock_code?: string;
  date_stock?: string;
  customer_order_id?: number;
  supplier_order_id?: number;
  udc_pallet?: string;
  udc_collo?: string;
  qta_available: number;
  qta_reserved: number;
  qta_in_production: number;
  qta_in_transit: number;
  cost?: number;
  price?: number;
  weight_gross?: number;
  created_at: string;
  updated_at: string;
  // Campi leggibili
  internal_product_code: string;
  client_product_code?: string;
  dogana_code?: string;
  product_description: string;
  size?: string;
  color?: string;
  customer_company?: string;
  supplier_name?: string;
  customs_document_number?: string;
  warehouse_description: string;
  total_quantity: number;
  // Nuovi campi per prenotazioni
  total_reservations: number;
  total_orders_with_reservations: number;
  has_reservations: boolean;
  available_reservations?: number;
  in_production_reservations?: number;
  in_transit_reservations?: number;
  supplier_delivery_date?: string;
  supplier_estimated_delivery_date?: string;
  supplier_order_status?: string;
  expected_availability_date?: string;
  // Campi container
  container_code?: string;
  container_description?: string;
  vehicle_plate?: string;
  departure_date?: string;
  actual_departure_date?: string;
  expected_arrival_date?: string;
  actual_arrival_date?: string;
  data_partenza?: string;
  data_prevista_arrivo?: string;
}

interface InventoryFilters {
  product_id?: number;
  stock_code?: string;
  customer_id?: number;
  customer_order_id?: number;
  supplier_id?: number;
  supplier_order_id?: number;
  warehouse_id?: number;
  udc_pallet?: string;
  udc_collo?: string;
  customs_document_number?: string;
  vehicle_plate?: string;
  invoice_number?: string;
  show_zero_quantities?: boolean;
  has_reservations?: boolean;
}

interface FilterOptions {
  products: Array<{id: number, internal_product_code: string, product_description: string, display_name: string}>;
  stock_codes: Array<{value: string}>;
  customers: Array<{id: number, company: string}>;
  suppliers: Array<{id: number, name: string}>;
  warehouses: Array<{id: number, description: string, cod_mag: string}>;
  customer_orders: Array<{id: number, order_number: string}>;
  supplier_orders: Array<{id: number, order_number: string}>;
}

const Inventory: React.FC = () => {
  const { canWrite, user } = useAuth();
  const canEdit = canWrite('inventory');
  // "Importa da Excel" (anagrafica prodotti da file giacenze): riservato all'amministratore
  const isAdmin = user?.level_id === 1;
  const [stocks, setStocks] = useState<InventoryStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStock, setEditingStock] = useState<InventoryStock | null>(null);
  const [filters, setFilters] = useState<InventoryFilters>({});
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [sorting, setSorting] = useState({
    sort_by: 'created_at',
    sort_direction: 'desc'
  });
  // Nuovo state per modal prenotazioni
  const [reservationsModalVisible, setReservationsModalVisible] = useState(false);
  const [selectedStock, setSelectedStock] = useState<InventoryStock | null>(null);

  // State per modal storia prodotto
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [stockToHistory, setStockToHistory] = useState<InventoryStock | null>(null);

  // State per modal suddivisione stock
  const [splitModalVisible, setSplitModalVisible] = useState(false);
  const [stockToSplit, setStockToSplit] = useState<InventoryStock | null>(null);

  // State per modal trasferimento magazzino
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [stockToTransfer, setStockToTransfer] = useState<InventoryStock | null>(null);

  // State per modal cambio UDC
  const [udcChangeModalVisible, setUdcChangeModalVisible] = useState(false);
  const [stockToChangeUdc, setStockToChangeUdc] = useState<InventoryStock | null>(null);

  // State per modal aggiustamento inventario
  const [adjustmentModalVisible, setAdjustmentModalVisible] = useState(false);
  const [stockToAdjust, setStockToAdjust] = useState<InventoryStock | null>(null);

  // State per export Excel
  const [exportLoading, setExportLoading] = useState(false);

  // State per import Excel
  const [importModalVisible, setImportModalVisible] = useState(false);

  // State per visualizzazione righe senza prodotti
  const [showZeroQuantities, setShowZeroQuantities] = useState(false);
  const [hasReservations, setHasReservations] = useState(false);

  // State per totali quantità
  const [totalQtaAvailable, setTotalQtaAvailable] = useState<number>(0);
  const [totalQtaInProduction, setTotalQtaInProduction] = useState<number>(0);
  const [totalQtaInTransit, setTotalQtaInTransit] = useState<number>(0);
  const [totalAvailableReservations, setTotalAvailableReservations] = useState<number>(0);
  const [totalInProductionReservations, setTotalInProductionReservations] = useState<number>(0);
  const [totalInTransitReservations, setTotalInTransitReservations] = useState<number>(0);

  // State per gestione visibilità colonne
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    internal_product_code: true,
    stock_code: true,
    dogana_code: true,
    date_stock: true,
    customs_document_number: true,
    warehouse_description: true,
    customer_company: true,
    supplier_name: true,
    udc: true,
    container: true,
    vehicle_plate: true,
    qta_available: true,
    qta_in_transit: true,
    qta_in_production: true,
    cost: true,
    price: true,
    weight_gross: true,
  });

  const [columnsVisible, setColumnsVisible] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // State per collassare colonna Azioni
  const [actionsExpanded, setActionsExpanded] = useState(true);

  // Carica opzioni per i filtri
  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await inventoryAPI.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading filter options:', error);
      message.error('Errore nel caricamento delle opzioni di filtro');
    }
  }, []);

  // Carica preferenze colonne
  const loadColumnPreferences = useCallback(async () => {
    try {
      const response = await userColumnPreferencesAPI.getPreferences('inventory');
      setColumnVisibility(response.columns);
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  }, []);

  // Carica stocks con filtri, paginazione e ordinamento
  const loadStocks = useCallback(async (page = 1, pageSize = 20, currentFilters = filters, currentSorting = sorting) => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      // Includi showZeroQuantities e hasReservations nei filtri
      const filtersWithOptions = { ...currentFilters, show_zero_quantities: showZeroQuantities, has_reservations: hasReservations };
      const response = await inventoryAPI.getInventoryStocks(filtersWithOptions, { skip, limit: pageSize }, currentSorting);
      
      setStocks(response.items);
      setTotalQtaAvailable(response.total_qta_available ?? 0);
      setTotalQtaInProduction(response.total_qta_in_production ?? 0);
      setTotalQtaInTransit(response.total_qta_in_transit ?? 0);
      setTotalAvailableReservations(response.total_available_reservations ?? 0);
      setTotalInProductionReservations(response.total_in_production_reservations ?? 0);
      setTotalInTransitReservations(response.total_in_transit_reservations ?? 0);
      setPagination({
        current: page,
        pageSize,
        total: response.total,
      });
    } catch (error) {
      console.error('Error loading stocks:', error);
      message.error('Errore nel caricamento dell\'inventario');
    } finally {
      setLoading(false);
    }
  }, [filters, sorting, showZeroQuantities, hasReservations]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFilterOptions();
    loadColumnPreferences();
    loadStocks();
  }, []);

  // useEffect per ricaricare quando cambia showZeroQuantities
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (filterOptions) { // Solo se le opzioni filtro sono già caricate
      loadStocks(1, pagination.pageSize, filters, sorting);
    }
  }, [showZeroQuantities]);

  // useEffect per ricaricare quando cambia hasReservations
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (filterOptions) {
      loadStocks(1, pagination.pageSize, filters, sorting);
    }
  }, [hasReservations]);

  // Gestione filtri
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    loadStocks(1, pagination.pageSize, filters, sorting);
  };

  const clearFilters = () => {
    setFilters({});
    loadStocks(1, pagination.pageSize, {}, sorting);
  };

  // Gestione modal
  const handleAddStock = () => {
    setEditingStock(null);
    setModalVisible(true);
  };

  const handleEditStock = (stock: InventoryStock) => {
    setEditingStock(stock);
    setModalVisible(true);
  };

  const handleDeleteStock = async (id: number) => {
    try {
      await inventoryAPI.deleteInventoryStock(id);
      message.success('Stock eliminato con successo');
      loadStocks(pagination.current, pagination.pageSize, filters, sorting);
    } catch (error: any) {
      console.error('Error deleting stock:', error);
      // Mostra il motivo del blocco (guardie integrità: quantità residue o prenotazioni)
      message.error(error?.response?.data?.detail || 'Errore nell\'eliminazione dello stock', 6);
    }
  };

  const handleModalOk = () => {
    setModalVisible(false);
    setEditingStock(null);
    loadStocks(pagination.current, pagination.pageSize, filters, sorting);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingStock(null);
  };

  // Gestione paginazione e ordinamento
  const handleTableChange = (page: number, pageSize?: number, sorter?: any) => {
    if (sorter && sorter.field) {
      // Gestione ordinamento
      const newSorting = {
        sort_by: sorter.field,
        sort_direction: sorter.order === 'ascend' ? 'asc' : 'desc'
      };
      setSorting(newSorting);
      loadStocks(page, pageSize || pagination.pageSize, filters, newSorting);
    } else {
      // Solo paginazione
      loadStocks(page, pageSize || pagination.pageSize, filters, sorting);
    }
  };

  // Gestione modal prenotazioni
  const handleViewReservations = (stock: InventoryStock) => {
    setSelectedStock(stock);
    setReservationsModalVisible(true);
  };

  const handleCloseReservationsModal = () => {
    setReservationsModalVisible(false);
    setSelectedStock(null);
  };

  // Gestione modal storia prodotto
  const handleShowHistory = (stock: InventoryStock) => {
    setStockToHistory(stock);
    setHistoryModalVisible(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalVisible(false);
    setStockToHistory(null);
  };

  // Gestione modal suddivisione stock
  const handleSplitStock = (stock: InventoryStock) => {
    setStockToSplit(stock);
    setSplitModalVisible(true);
  };

  const handleSplitModalOk = () => {
    setSplitModalVisible(false);
    setStockToSplit(null);
    loadStocks(pagination.current, pagination.pageSize);
  };

  const handleSplitModalCancel = () => {
    setSplitModalVisible(false);
    setStockToSplit(null);
  };

  // Gestione modal trasferimento magazzino
  const handleTransferStock = (stock: InventoryStock) => {
    setStockToTransfer(stock);
    setTransferModalVisible(true);
  };

  const handleTransferModalOk = () => {
    setTransferModalVisible(false);
    setStockToTransfer(null);
    loadStocks(pagination.current, pagination.pageSize);
  };

  const handleTransferModalCancel = () => {
    setTransferModalVisible(false);
    setStockToTransfer(null);
  };

  // Gestione cambio UDC
  const handleChangeUdc = (record: InventoryStock) => {
    setStockToChangeUdc(record);
    setUdcChangeModalVisible(true);
  };

  const handleUdcChangeModalOk = () => {
    setUdcChangeModalVisible(false);
    setStockToChangeUdc(null);
    loadStocks(pagination.current, pagination.pageSize);
  };

  const handleUdcChangeModalCancel = () => {
    setUdcChangeModalVisible(false);
    setStockToChangeUdc(null);
  };

  // Gestione aggiustamento inventario
  const handleAdjustStock = (record: InventoryStock) => {
    setStockToAdjust(record);
    setAdjustmentModalVisible(true);
  };

  const handleAdjustmentModalOk = () => {
    setAdjustmentModalVisible(false);
    setStockToAdjust(null);
    loadStocks(pagination.current, pagination.pageSize);
  };

  const handleAdjustmentModalCancel = () => {
    setAdjustmentModalVisible(false);
    setStockToAdjust(null);
  };

  // Gestione export Excel
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const filtersWithOptions = { ...filters, show_zero_quantities: showZeroQuantities, has_reservations: hasReservations };
      await inventoryAPI.exportInventoryExcel(filtersWithOptions, sorting);
      message.success('Export Excel completato con successo');
    } catch (error: any) {
      console.error('Errore export Excel:', error);
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'export Excel';
      message.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  // Gestione checkbox visualizzazione righe senza prodotti
  const handleShowZeroQuantitiesChange = (checked: boolean) => {
    setShowZeroQuantities(checked);
    // Non chiamiamo loadStocks qui, sarà richiamato automaticamente dal useEffect
  };

  // Gestione cambio visibilità colonne
  const handleColumnVisibilityChange = async (columnKey: string, checked: boolean) => {
    const newVisibility = { ...columnVisibility, [columnKey]: checked };
    setColumnVisibility(newVisibility);

    setSavingPreferences(true);
    try {
      await userColumnPreferencesAPI.savePreferences('inventory', newVisibility);
      message.success('Preferenze colonne salvate');
    } catch (error: any) {
      console.error('Error saving column preferences:', error);
      message.error('Errore nel salvataggio delle preferenze');
      setColumnVisibility(columnVisibility);
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleResetColumnPreferences = async () => {
    try {
      const response = await userColumnPreferencesAPI.resetPreferences('inventory');
      setColumnVisibility(response.defaults);
      message.success('Preferenze ripristinate ai valori default');
    } catch (error: any) {
      console.error('Error resetting column preferences:', error);
      message.error('Errore nel ripristino delle preferenze');
    }
  };

  // Render tag per quantità con prenotazioni
  const renderQuantityWithReservations = (
    quantity: number, 
    type: 'available' | 'in_transit' | 'in_production',
    stock: InventoryStock
  ) => {
    let color = 'default';
    let reservations = 0;
    
    // Calcola la quantità totale e le prenotazioni per il tipo specifico
    if (type === 'available') {
      color = quantity > 0 ? 'green' : 'default';
      reservations = stock.available_reservations || 0;
    } else if (type === 'in_transit') {
      color = quantity > 0 ? 'orange' : 'default';
      reservations = stock.in_transit_reservations || 0;
    } else if (type === 'in_production') {
      color = quantity > 0 ? 'blue' : 'default';
      reservations = stock.in_production_reservations || 0;
    }
    
    const totalQuantity = quantity;
    const displayText = reservations > 0 
      ? `${totalQuantity} (di cui Opz. ${reservations})`
      : totalQuantity.toString();
    
    return (
      <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
        <Tag color={color}>{displayText}</Tag>
        {(type === 'in_production' || type === 'in_transit') && stock.expected_availability_date && totalQuantity > 0 && (
          <Tag color="purple" style={{ fontSize: '11px' }}>
            {type === 'in_production' ? 'Consegna:' : 'Arrivo:'} {new Date(stock.expected_availability_date).toLocaleDateString('it-IT')}
          </Tag>
        )}
        {type === 'in_transit' && totalQuantity > 0 && stock.transit_shipment_code && (
          <Tag color="cyan" style={{ fontSize: '11px' }}>
            {stock.transit_shipment_code}
          </Tag>
        )}
      </Space>
    );
  };

  // Genera identificatore composto per prodotto
  const generateCompositeIdentifier = (internal_code: string, size?: string, color?: string) => {
    if (!size && !color) {
      return internal_code;
    } else if (size && !color) {
      return `${internal_code}-${size}`;
    } else if (!size && color) {
      return `${internal_code}--${color}`;
    } else {
      return `${internal_code}-${size}-${color}`;
    }
  };

  // Definizione colonne tabella con ordinamento
  const allColumns = [
    {
      title: 'Prodotto',
      dataIndex: 'internal_product_code',
      key: 'internal_product_code',
      width: 300,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string, record: InventoryStock) => {
        const compositeIdentifier = generateCompositeIdentifier(
          record.internal_product_code,
          record.size,
          record.color
        );

        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{compositeIdentifier}</div>
            {record.client_product_code && (
              <div style={{ fontSize: '13px', color: '#333', marginTop: '2px' }}>
                {record.client_product_code}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.product_description}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'Data Stock',
      dataIndex: 'date_stock',
      key: 'date_stock',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('it-IT') : '-',
    },
    {
      title: 'Cod. Dogana',
      dataIndex: 'dogana_code',
      key: 'dogana_code',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'Doc. Carico',
      dataIndex: 'customs_document_number',
      key: 'customs_document_number',
      width: 180,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'Magazzino',
      dataIndex: 'warehouse_description',
      key: 'warehouse_description',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_company',
      key: 'customer_company',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'Fornitore',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'UDC',
      key: 'udc',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: InventoryStock) => {
        if (!record.udc_pallet && !record.udc_collo) {
          return <Tag color="default">Nessuno</Tag>;
        }
        return (
          <Space direction="vertical" size="small">
            {record.udc_pallet && <Tag color="blue">Pallet: {record.udc_pallet}</Tag>}
            {record.udc_collo && <Tag color="cyan">Collo: {record.udc_collo}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Container',
      key: 'container',
      width: 200,
      render: (record: InventoryStock) => {
        if (!record.container_code && !record.container_description && !record.vehicle_plate) {
          return '-';
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {record.vehicle_plate && (
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                {record.vehicle_plate}
              </div>
            )}
            {record.container_code && (
              <div style={{ marginBottom: '2px' }}>
                {record.container_code}
              </div>
            )}
            {record.data_partenza && (
              <div style={{ color: '#1890ff', marginBottom: '1px' }}>
                Partenza: {new Date(record.data_partenza).toLocaleDateString('it-IT')}
              </div>
            )}
            {record.data_prevista_arrivo && (
              <div style={{ color: '#52c41a' }}>
                Arrivo: {new Date(record.data_prevista_arrivo).toLocaleDateString('it-IT')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Targa/N° Container',
      dataIndex: 'vehicle_plate',
      key: 'vehicle_plate',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (text: string) => text || '-',
    },
    {
      title: 'Disponibile',
      dataIndex: 'qta_available',
      key: 'qta_available',
      width: 140,
      align: 'center' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (quantity: number, record: InventoryStock) => 
        renderQuantityWithReservations(quantity, 'available', record),
    },
    {
      title: 'In Transito',
      dataIndex: 'qta_in_transit',
      key: 'qta_in_transit',
      width: 140,
      align: 'center' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (quantity: number, record: InventoryStock) => 
        renderQuantityWithReservations(quantity, 'in_transit', record),
    },
    {
      title: 'In Produzione',
      dataIndex: 'qta_in_production',
      key: 'qta_in_production',
      width: 140,
      align: 'center' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (quantity: number, record: InventoryStock) => 
        renderQuantityWithReservations(quantity, 'in_production', record),
    },
    {
      title: 'Costo',
      dataIndex: 'cost',
      key: 'cost',
      width: 80,
      align: 'right' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (value: number) => value ? `€ ${formatCurrency(value)}` : '-',
    },
    {
      title: 'Vendita',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      align: 'right' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (value: number) => value ? `€ ${formatCurrency(value)}` : '-',
    },
    {
      title: 'Peso Lordo',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      width: 100,
      align: 'right' as const,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as const,
      render: (value: number) => value ? `${value.toLocaleString('it-IT', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} kg` : '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      fixed: 'right' as const,
      width: actionsExpanded ? 280 : 80,
      render: (record: InventoryStock) => (
        <Space size="small" wrap={actionsExpanded} direction={actionsExpanded ? 'horizontal' : 'vertical'}>
          <Button
            type="default"
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleShowHistory(record)}
            title="Storia del prodotto"
          >
            {actionsExpanded && 'Storia'}
          </Button>

          {record.has_reservations && (
            <Badge count={record.total_orders_with_reservations} size="small">
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewReservations(record)}
                title="Visualizza prenotazioni"
              >
                {actionsExpanded && 'Prenot.'}
              </Button>
            </Badge>
          )}

          {canEdit && record.qta_available > 0 && (
            <Button
              type="default"
              size="small"
              icon={<SplitCellsOutlined />}
              onClick={() => handleSplitStock(record)}
              title="Suddividi in UDC"
            >
              {actionsExpanded && 'Suddiv.'}
            </Button>
          )}

          {canEdit && record.qta_available > 0 && (
            <Button
              type="default"
              size="small"
              icon={<SwapOutlined />}
              onClick={() => handleTransferStock(record)}
              title="Trasferisci magazzino"
            >
              {actionsExpanded && 'Trasf.'}
            </Button>
          )}

          {canEdit && (
            <Button
              type="default"
              size="small"
              icon={<TagOutlined />}
              onClick={() => handleChangeUdc(record)}
              title="Sposta tra UDC"
            >
              {actionsExpanded && 'UDC'}
            </Button>
          )}

          {canEdit && (
            <Button
              type="default"
              size="small"
              icon={<ToolOutlined />}
              onClick={() => handleAdjustStock(record)}
              title="Aggiustamento quantità"
            >
              {actionsExpanded && 'Agg.'}
            </Button>
          )}

          {canEdit && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditStock(record)}
              title="Modifica stock"
            >
              {actionsExpanded && 'Modifica'}
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo stock?"
              onConfirm={() => handleDeleteStock(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                title="Elimina stock"
              >
                {actionsExpanded && 'Elimina'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Filtra colonne in base a visibilità
  const columns = allColumns.filter(col => {
    if (col.key === 'actions') return true; // Azioni sempre visibile
    return columnVisibility[col.key as string] !== false;
  });

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Inventario</Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersVisible(!filtersVisible)}
              >
                {filtersVisible ? 'Nascondi Filtri' : 'Mostra Filtri'}
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setColumnsVisible(!columnsVisible)}
              >
                {columnsVisible ? 'Nascondi Colonne' : 'Gestisci Colonne'}
              </Button>
              <Button
                icon={actionsExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                onClick={() => setActionsExpanded(!actionsExpanded)}
                title={actionsExpanded ? 'Comprimi colonna Azioni' : 'Espandi colonna Azioni'}
              >
                {actionsExpanded ? 'Comprimi Azioni' : 'Espandi Azioni'}
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={handleExportExcel}
                loading={exportLoading}
                title="Esporta inventario in Excel con filtri applicati"
              >
                Esporta in Excel
              </Button>
              {isAdmin && (
                <Button
                  icon={<ImportOutlined />}
                  onClick={() => setImportModalVisible(true)}
                  title="Importa inventario da file Excel"
                >
                  Importa da Excel
                </Button>
              )}
              {canEdit && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddStock}
                >
                  Nuovo Prodotto in Stock
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {filtersVisible && (
          <Collapse style={{ marginBottom: '16px' }} defaultActiveKey={['1']}>
            <Panel header="Filtri di Ricerca" key="1">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Seleziona Prodotto"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.product_id}
                    onChange={(value) => handleFilterChange('product_id', value)}
                    filterOption={(input, option) =>
                      (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {filterOptions?.products.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.display_name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Seleziona Stock Code"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.stock_code}
                    onChange={(value) => handleFilterChange('stock_code', value)}
                  >
                    {filterOptions?.stock_codes.map(stock => (
                      <Option key={stock.value} value={stock.value}>
                        {stock.value}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Seleziona Cliente"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.customer_id}
                    onChange={(value) => handleFilterChange('customer_id', value)}
                    filterOption={(input, option) =>
                      (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {filterOptions?.customers.map(customer => (
                      <Option key={customer.id} value={customer.id}>
                        {customer.company}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Seleziona Fornitore"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.supplier_id}
                    onChange={(value) => handleFilterChange('supplier_id', value)}
                    filterOption={(input, option) =>
                      (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {filterOptions?.suppliers.map(supplier => (
                      <Option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Documento di Carico"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.customs_document_number}
                    onChange={(e) => handleFilterChange('customs_document_number', e.target.value || undefined)}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Targa / N° Container (spazi ignorati)"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.vehicle_plate}
                    onChange={(e) => handleFilterChange('vehicle_plate', e.target.value || undefined)}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Fattura / Packing list fornitore"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.invoice_number}
                    onChange={(e) => handleFilterChange('invoice_number', e.target.value || undefined)}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Seleziona Magazzino"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.warehouse_id}
                    onChange={(value) => handleFilterChange('warehouse_id', value)}
                  >
                    {filterOptions?.warehouses.map(warehouse => (
                      <Option key={warehouse.id} value={warehouse.id}>
                        {warehouse.description}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="UDC Pallet"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.udc_pallet}
                    onChange={(e) => handleFilterChange('udc_pallet', e.target.value || undefined)}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="UDC Collo"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.udc_collo}
                    onChange={(e) => handleFilterChange('udc_collo', e.target.value || undefined)}
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Ordine Cliente"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.customer_order_id}
                    onChange={(value) => handleFilterChange('customer_order_id', value)}
                  >
                    {filterOptions?.customer_orders.map(order => (
                      <Option key={order.id} value={order.id}>
                        {order.order_number}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Ordine Fornitore"
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    value={filters.supplier_order_id}
                    onChange={(value) => handleFilterChange('supplier_order_id', value)}
                  >
                    {filterOptions?.supplier_orders.map(order => (
                      <Option key={order.id} value={order.id}>
                        {order.order_number}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>
                      Applica Filtri
                    </Button>
                    <Button onClick={clearFilters}>
                      Pulisci
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        )}

        {columnsVisible && (
          <Collapse style={{ marginBottom: '16px' }} defaultActiveKey={['1']}>
            <Panel header="Gestione Colonne" key="1">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                      Seleziona le colonne da visualizzare:
                    </div>

                    <Row gutter={[8, 8]}>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.internal_product_code}
                          onChange={(e) => handleColumnVisibilityChange('internal_product_code', e.target.checked)}
                        >
                          Prodotto
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.stock_code}
                          onChange={(e) => handleColumnVisibilityChange('stock_code', e.target.checked)}
                        >
                          Stock Code
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.dogana_code}
                          onChange={(e) => handleColumnVisibilityChange('dogana_code', e.target.checked)}
                        >
                          Cod. Dogana
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.date_stock}
                          onChange={(e) => handleColumnVisibilityChange('date_stock', e.target.checked)}
                        >
                          Data Stock
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.customs_document_number}
                          onChange={(e) => handleColumnVisibilityChange('customs_document_number', e.target.checked)}
                        >
                          Doc. Carico
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.vehicle_plate}
                          onChange={(e) => handleColumnVisibilityChange('vehicle_plate', e.target.checked)}
                        >
                          Targa/N° Container
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.warehouse_description}
                          onChange={(e) => handleColumnVisibilityChange('warehouse_description', e.target.checked)}
                        >
                          Magazzino
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.customer_company}
                          onChange={(e) => handleColumnVisibilityChange('customer_company', e.target.checked)}
                        >
                          Cliente
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.supplier_name}
                          onChange={(e) => handleColumnVisibilityChange('supplier_name', e.target.checked)}
                        >
                          Fornitore
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.udc}
                          onChange={(e) => handleColumnVisibilityChange('udc', e.target.checked)}
                        >
                          UDC
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.container}
                          onChange={(e) => handleColumnVisibilityChange('container', e.target.checked)}
                        >
                          Container
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.qta_available}
                          onChange={(e) => handleColumnVisibilityChange('qta_available', e.target.checked)}
                        >
                          Disponibile
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.qta_in_transit}
                          onChange={(e) => handleColumnVisibilityChange('qta_in_transit', e.target.checked)}
                        >
                          In Transito
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.qta_in_production}
                          onChange={(e) => handleColumnVisibilityChange('qta_in_production', e.target.checked)}
                        >
                          In Produzione
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.cost}
                          onChange={(e) => handleColumnVisibilityChange('cost', e.target.checked)}
                        >
                          Costo
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.price}
                          onChange={(e) => handleColumnVisibilityChange('price', e.target.checked)}
                        >
                          Vendita
                        </Checkbox>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Checkbox
                          checked={columnVisibility.weight_gross}
                          onChange={(e) => handleColumnVisibilityChange('weight_gross', e.target.checked)}
                        >
                          Peso Lordo
                        </Checkbox>
                      </Col>
                    </Row>

                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                      <Space>
                        <Button onClick={handleResetColumnPreferences} disabled={savingPreferences}>
                          Ripristina Default
                        </Button>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {savingPreferences ? 'Salvataggio in corso...' : 'Le modifiche vengono salvate automaticamente'}
                        </span>
                      </Space>
                    </div>
                  </Space>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        )}

        {/* Checkbox per visualizzazione righe senza prodotti + Totale quantità disponibile */}
        <Row style={{ marginBottom: '16px' }} align="middle">
          <Col>
            <Checkbox
              checked={showZeroQuantities}
              onChange={(e) => handleShowZeroQuantitiesChange(e.target.checked)}
            >
              Visualizza righe senza prodotti
            </Checkbox>
          </Col>
          <Col style={{ marginLeft: 16 }}>
            <Checkbox
              checked={hasReservations}
              onChange={(e) => setHasReservations(e.target.checked)}
            >
              Solo con prenotazioni
            </Checkbox>
          </Col>
          <Col style={{ marginLeft: 24 }}>
            <div>
              <span style={{ fontWeight: 'bold' }}>Quantità Disponibile: </span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                {totalQtaAvailable.toLocaleString('it-IT')}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Opzionata: <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>{totalAvailableReservations.toLocaleString('it-IT')}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Opzionabile: <span style={{ fontWeight: 'bold', color: '#52c41a' }}>{(totalQtaAvailable - totalAvailableReservations).toLocaleString('it-IT')}</span>
            </div>
          </Col>
          <Col style={{ marginLeft: 24 }}>
            <div>
              <span style={{ fontWeight: 'bold' }}>In Produzione: </span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                {totalQtaInProduction.toLocaleString('it-IT')}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Opzionata: <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>{totalInProductionReservations.toLocaleString('it-IT')}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Opzionabile: <span style={{ fontWeight: 'bold', color: '#52c41a' }}>{(totalQtaInProduction - totalInProductionReservations).toLocaleString('it-IT')}</span>
            </div>
          </Col>
          <Col style={{ marginLeft: 24 }}>
            <div>
              <span style={{ fontWeight: 'bold' }}>In Transito: </span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                {totalQtaInTransit.toLocaleString('it-IT')}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Opzionata: <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>{totalInTransitReservations.toLocaleString('it-IT')}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Opzionabile: <span style={{ fontWeight: 'bold', color: '#52c41a' }}>{(totalQtaInTransit - totalInTransitReservations).toLocaleString('it-IT')}</span>
            </div>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={stocks}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          onChange={(pagination, filters, sorter) => {
            if (pagination) {
              handleTableChange(pagination.current || 1, pagination.pageSize, sorter);
            }
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} di ${total} elementi`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      <InventoryStockModal
        visible={modalVisible}
        stock={editingStock}
        filterOptions={filterOptions}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      />

      <StockReservationsModal
        visible={reservationsModalVisible}
        stockId={selectedStock?.id || null}
        stockCode={selectedStock?.stock_code}
        productDescription={selectedStock?.product_description}
        onClose={handleCloseReservationsModal}
      />

      <StockHistoryModal
        visible={historyModalVisible}
        stockId={stockToHistory?.id || null}
        stockCode={stockToHistory?.stock_code}
        productDescription={stockToHistory?.product_description}
        onClose={handleCloseHistoryModal}
      />

      <StockSplitModal
        visible={splitModalVisible}
        stockId={stockToSplit?.id || null}
        stockCode={stockToSplit?.stock_code}
        productDescription={stockToSplit?.product_description}
        availableQuantity={stockToSplit?.qta_available || 0}
        onOk={handleSplitModalOk}
        onCancel={handleSplitModalCancel}
      />

      <WarehouseTransferModal
        visible={transferModalVisible}
        stockId={stockToTransfer?.id || null}
        productDescription={stockToTransfer?.product_description}
        currentWarehouseId={stockToTransfer?.warehouse_id}
        currentWarehouseName={stockToTransfer?.warehouse_description}
        availableQuantity={stockToTransfer?.qta_available || 0}
        onOk={handleTransferModalOk}
        onCancel={handleTransferModalCancel}
      />

      <UdcChangeModal
        visible={udcChangeModalVisible}
        stockId={stockToChangeUdc?.id || null}
        productDescription={stockToChangeUdc?.product_description}
        currentUdcPallet={stockToChangeUdc?.udc_pallet}
        currentUdcCollo={stockToChangeUdc?.udc_collo}
        availableQuantity={stockToChangeUdc?.qta_available || 0}
        onOk={handleUdcChangeModalOk}
        onCancel={handleUdcChangeModalCancel}
      />

      <InventoryAdjustmentModal
        visible={adjustmentModalVisible}
        stockId={stockToAdjust?.id || null}
        productDescription={stockToAdjust?.product_description}
        stockCode={stockToAdjust?.stock_code}
        availableQuantity={stockToAdjust?.qta_available || 0}
        inProductionQuantity={stockToAdjust?.qta_in_production || 0}
        inTransitQuantity={stockToAdjust?.qta_in_transit || 0}
        onOk={handleAdjustmentModalOk}
        onCancel={handleAdjustmentModalCancel}
      />

      <InventoryImportModal
        visible={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        onSuccess={() => {
          loadStocks(pagination.current, pagination.pageSize, filters, sorting);
        }}
      />
    </div>
  );
};

export default Inventory;